
import React, { useState, useEffect } from 'react';
import { Bell, X, Info, CheckCircle, AlertTriangle, Zap, ArrowRight, Archive, ShieldAlert, Sparkles, Loader2 } from 'lucide-react';
import { useAuth } from './AuthBridge';
import { NotificationService, NotificationItem } from '../services/notificationService';
import { useNavigate } from 'react-router';
import { PageLoader } from './PageLoader';
import { useThemeTokens } from '../hooks/useActiveTheme';

export const NotificationCenter: React.FC = () => {
    const { user, activeCert } = useAuth();
    const navigate = useNavigate();
    const tokens = useThemeTokens();
    const [notifications, setNotifications] = useState<NotificationItem[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const loadNotifications = async () => {
        if (!user?.id) return;
        setIsLoading(true);
        const data = await NotificationService.getNotifications(user.id);
        setNotifications(data);
        setIsLoading(false);
    };

    useEffect(() => {
        if (user?.id) loadNotifications();
        const interval = setInterval(() => { if (user?.id) loadNotifications(); }, 60000);
        return () => clearInterval(interval);
    }, [user?.id]);

    const handleAction = async (item: NotificationItem, action: 'read' | 'dismiss') => {
        if (!user?.id) return;
        
        // Mark in DB
        await NotificationService.updateStatus(user.id, item._id, action);
        
        // Remove from local list immediately
        setNotifications(prev => prev.filter(n => n._id !== item._id));
        
        if (action === 'read' && item.link) {
            // 1. External Link Protocol
            if (item.link.startsWith('http')) {
                window.open(item.link, '_blank', 'noopener,noreferrer');
                setIsOpen(false);
                return;
            }

            // 2. Internal Route Normalization & Elite Override
            let finalLink = item.link;
            
            // Force Elite path for relevant notifications
            const isEliteNotice = item.type === 'pro' || 
                                 item.title.toLowerCase().includes('elite') || 
                                 item.message.toLowerCase().includes('elite');

            if (isEliteNotice) {
                finalLink = '/elite';
            } else {
                // Global sanitization: strip any string "undefined" fragments anywhere in the path
                finalLink = finalLink.replace(/undefined/g, '');
            }
            
            // Ensure leading slash to prevent relative path appending in HashRouter
            if (!finalLink.startsWith('/')) {
                finalLink = '/' + finalLink;
            }

            let currentCertId = activeCert?.id || user?.activeCertId || 'cna';
            if (currentCertId === 'undefined') currentCertId = 'cna';

            // 3. Namespace Prefixing Logic
            // Prepend certId if it's a clinical route missing the prefix and not an admin/system path
            if (!finalLink.startsWith('/manage') && 
                !finalLink.startsWith('/404') && 
                !finalLink.startsWith(`/${currentCertId}`)) {
                finalLink = `/${currentCertId}${finalLink}`;
            }

            // 4. Final Path Cleaning
            finalLink = finalLink.replace(/\/+/g, '/'); // Strip double slashes
            if (finalLink.endsWith('/') && finalLink.length > 1) finalLink = finalLink.slice(0, -1);

            // Safety check: if path became empty or just a slash, default to dashboard
            if (finalLink === '/' || finalLink === `/${currentCertId}`) {
                finalLink = `/${currentCertId}/dashboard`;
            }

            navigate(finalLink);
            setIsOpen(false);
        }
    };

    const unreadCount = notifications.filter(n => !n.isRead).length;

    const getTypeStyles = (type: string) => {
        switch(type) {
            case 'success': return 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400 border-emerald-100 dark:border-emerald-800';
            case 'warning': return 'bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400 border-amber-100 dark:border-amber-800';
            case 'pro': return `${tokens.badgeBg} ${tokens.accentText}`;
            case 'remediation': return 'bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400 border-rose-100 dark:border-rose-800';
            default: return `${tokens.badgeBg} ${tokens.accentText}`;
        }
    };

    return (
        <div className="relative">
            <button 
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-xl transition-all active:scale-95 group"
            >
                <Bell size={20} className={unreadCount > 0 ? 'animate-[swing_2s_ease-in-out_infinite]' : ''} />
                {unreadCount > 0 && (
                    <span className={`absolute -top-1 -right-1 w-5 h-5 ${tokens.isHighContrast ? 'bg-black text-amber-400 border-black' : 'bg-rose-500 text-white border-white dark:border-slate-900'} text-[10px] font-black rounded-full flex items-center justify-center border-2 animate-pop shadow-lg`}>
                        {unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <>
                    <div className="fixed inset-0 z-[160]" onClick={() => setIsOpen(false)} />
                    <div className={`fixed left-4 right-4 md:absolute md:left-auto md:right-0 mt-3 md:w-96 rounded-[2rem] shadow-[0_30px_80px_rgba(0,0,0,0.3)] border z-[170] overflow-hidden animate-pop ${
                        tokens.isHighContrast 
                          ? 'bg-white border-2 border-black text-black' 
                          : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white'
                    }`}>
                        <div className={`p-6 ${tokens.heroBannerBg} text-white flex justify-between items-center`}>
                            <div className="flex items-center gap-3">
                                <Sparkles size={18} className={tokens.accentText} />
                                <h4 className="text-sm font-black tracking-tight">Clinical Alerts</h4>
                            </div>
                            <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-white/10 rounded-lg"><X size={18}/></button>
                        </div>

                        <div className="max-h-[450px] overflow-y-auto custom-scrollbar p-2 space-y-2 bg-slate-50/50 dark:bg-slate-950/50">
                            {isLoading && notifications.length === 0 ? (
                                <PageLoader compact message="Loading Notifications..." />
                            ) : notifications.length === 0 ? (
                                <div className="p-12 text-center space-y-4 grayscale opacity-40">
                                    <ShieldAlert size={48} className="mx-auto" />
                                    <p className="text-[10px] font-black tracking-widest">Logic Stream Clear</p>
                                </div>
                            ) : (
                                notifications.map(item => (
                                    <div 
                                        key={item._id} 
                                        onClick={() => handleAction(item, 'read')}
                                        className={`p-4 rounded-2xl border-2 transition-all cursor-pointer relative group ${
                                            item.isRead 
                                                ? 'bg-white dark:bg-slate-800/60 border-transparent' 
                                                : `bg-white dark:bg-slate-800 ${tokens.accentBorder} shadow-md`
                                        }`}
                                    >
                                        <div className="flex gap-4">
                                            <div className={`p-2.5 rounded-xl shrink-0 h-fit ${getTypeStyles(item.type)}`}>
                                                {item.type === 'success' ? <CheckCircle size={16}/> : 
                                                 item.type === 'warning' ? <AlertTriangle size={16}/> : 
                                                 item.type === 'pro' ? <Zap size={16}/> : <Info size={16}/>}
                                            </div>
                                            <div className="flex-1 min-w-0 pr-6">
                                                <h5 className="font-black text-xs tracking-tight text-slate-800 dark:text-slate-100 line-clamp-1">{item.title}</h5>
                                                <p className="text-[11px] text-slate-500 dark:text-slate-300 font-medium leading-relaxed mt-1">{item.message}</p>
                                                <span className="text-[8px] font-black text-slate-300 dark:text-slate-500 mt-2 block">{new Date(item.createdAt).toLocaleDateString()}</span>
                                            </div>
                                        </div>
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); handleAction(item, 'dismiss'); }}
                                            className="absolute top-3 right-3 p-1.5 text-slate-400 hover:text-rose-500 transition-all active:scale-90"
                                            title="Dismiss notification"
                                        ><X size={16}/></button>
                                    </div>
                                ))
                            )}
                        </div>
                        
                        <div className="p-4 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 text-center">
                            <button 
                                onClick={() => { navigate(`/${activeCert?.id || user?.activeCertId || 'cna'}/profile`); setIsOpen(false); }}
                                className={`text-[9px] font-black ${tokens.accentText} tracking-widest hover:underline flex items-center justify-center gap-2 mx-auto`}
                            >
                                <Archive size={12}/> View Archive History
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
